import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem, FormControl,
    Paper, Divider, CircularProgress, Alert, Stack, Checkbox, FormControlLabel,
    Chip, Autocomplete
} from '@mui/material';
import * as api from './usersApi';
import type {
    User, Role, AddressType, ContactInfoType, Address, ContactInfo,
    Location, Brand
} from './usersApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface UserViewProps {
    userId: number | null;
}

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

export function UserView({ userId }: UserViewProps) {
    const { t } = useTranslation();

    const [user, setUser] = useState<User | null>(null);
    const [addressTypes, setAddressTypes] = useState<AddressType[]>([]);
    const [contactInfoTypes, setContactInfoTypes] = useState<ContactInfoType[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);

    const [isEditing, setIsEditing] = useState(false);
    const [isNewUser, setIsNewUser] = useState(false);
    const [userCopy, setUserCopy] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isNew = userId === null;

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [addressTypesData, contactInfoTypesData, rolesData, locationsData, brandsData] =
                await Promise.all([
                    api.getAddressTypes(),
                    api.getContactInfoTypes(),
                    api.getValidRoles(),
                    api.getLocations(),
                    api.getBrands(),
                ]);

            setAddressTypes(addressTypesData);
            setContactInfoTypes(contactInfoTypesData);
            setRoles(rolesData);
            setLocations(locationsData);
            setBrands(brandsData);

            if (isNew) {
                const emailType = contactInfoTypesData.find(x => x.systemTypeName === 'Email');
                const newUser: User = {
                    id: -1,
                    firstName: '',
                    lastName: '',
                    username: '',
                    isActive: true,
                    role: null,
                    addresses: [],
                    contactInfos: emailType ? [{ contactInfoType: emailType } as ContactInfo] : [],
                    locations: [],
                    brands: [],
                };
                setUser(newUser);
                setIsNewUser(true);
                setIsEditing(true);
            } else {
                const userData = await api.getUser(userId!);
                userData.addresses = userData.addresses ?? [];
                userData.contactInfos = userData.contactInfos ?? [];
                userData.locations = userData.locations ?? [];
                userData.brands = userData.brands ?? [];
                setUser(userData);
                setIsNewUser(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [userId, isNew]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }

    if (error && !user) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!user) return null;

    // Validation
    const hasFirstName = (): boolean => !!user.firstName && user.firstName.length > 0;
    const hasLastName = (): boolean => !!user.lastName && user.lastName.length > 0;
    const hasUserName = (): boolean => !!user.username && user.username.length > 0;
    const hasPassword = (): boolean => !isNewUser || (!!user.password && user.password.length > 0);
    const hasRole = (): boolean => !!user.role && user.role.id !== -1;
    const emailInfo = user.contactInfos.find(c => c.contactInfoType?.systemTypeName === 'Email');
    const hasEmail = (): boolean => !!emailInfo;
    const isEmailValid = (): boolean =>
        !!emailInfo?.value && EMAIL_REGEX.test(emailInfo.value);

    const isAllInformationValid = (): boolean =>
        hasFirstName() && hasLastName() && hasUserName() && hasPassword() &&
        hasEmail() && hasRole() && isEmailValid();

    const availableAddressTypes = addressTypes.filter(at =>
        !user.addresses.some(a => a.addressType?.id === at.id)
    );

    const availableContactInfoTypes = contactInfoTypes.filter(ct =>
        !user.contactInfos.some(c => c.contactInfoType?.id === ct.id)
    );

    // Actions
    const startEdit = () => {
        setUserCopy(JSON.parse(JSON.stringify(user)));
        setIsEditing(true);
    };

    const cancelEdit = () => {
        if (userCopy) setUser(userCopy);
        setIsEditing(false);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload: Partial<User> = {
                ...user,
                brands: user.brands.map(b => ({ id: b.id } as Brand)),
                locations: user.locations.map(l => ({ id: l.id } as Location)),
            };

            if (!payload.startDate) {
                const today = new Date();
                const yyyy = today.getFullYear();
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const dd = String(today.getDate()).padStart(2, '0');
                payload.startDate = `${yyyy}-${mm}-${dd}`;
            }

            if (isNewUser) {
                const response = await api.createUser(payload);
                response.addresses = response.addresses ?? [];
                response.contactInfos = response.contactInfos ?? [];
                response.locations = response.locations ?? [];
                response.brands = response.brands ?? [];
                setUser(response);
                setIsNewUser(false);
                setIsEditing(false);
            } else {
                const response = await api.updateUser(user.id, payload);
                response.addresses = response.addresses ?? [];
                response.contactInfos = response.contactInfos ?? [];
                response.locations = response.locations ?? [];
                response.brands = response.brands ?? [];
                setUser(response);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const updateUserField = <K extends keyof User>(field: K, value: User[K]) => {
        setUser({ ...user, [field]: value });
    };

    const addNewAddress = (addressType: AddressType) => {
        updateUserField('addresses', [...user.addresses, { addressType } as Address]);
    };

    const removeAddress = (address: Address) => {
        updateUserField('addresses', user.addresses.filter(a => a.addressType?.id !== address.addressType?.id));
    };

    const updateAddressField = (address: Address, field: string, value: string) => {
        updateUserField('addresses', user.addresses.map(a =>
            a.addressType?.id === address.addressType?.id ? { ...a, [field]: value } : a
        ));
    };

    const addNewContactInfo = (contactInfoType: ContactInfoType) => {
        updateUserField('contactInfos', [...user.contactInfos, { contactInfoType } as ContactInfo]);
    };

    const removeContactInfo = (info: ContactInfo) => {
        updateUserField('contactInfos', user.contactInfos.filter(c => c.contactInfoType?.id !== info.contactInfoType?.id));
    };

    const updateContactInfoField = (info: ContactInfo, field: string, value: string) => {
        updateUserField('contactInfos', user.contactInfos.map(c =>
            c.contactInfoType?.id === info.contactInfoType?.id ? { ...c, [field]: value } : c
        ));
    };

    const displayName = isNewUser ? t('user.new') : `${user.firstName} ${user.lastName}`;

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={displayName}
                isEditing={isEditing}
                isNew={isNewUser}
                saving={saving}
                canSave={isAllInformationValid()}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    {/* First Name */}
                    <FormRow label={t('user.firstname')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.firstName}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={user.firstName}
                                onChange={e => updateUserField('firstName', e.target.value)}
                                error={!hasFirstName()}
                            />
                        )}
                    </FormRow>

                    {/* Last Name */}
                    <FormRow label={t('user.lastname')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.lastName}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={user.lastName}
                                onChange={e => updateUserField('lastName', e.target.value)}
                                error={!hasLastName()}
                            />
                        )}
                    </FormRow>

                    {/* Username */}
                    <FormRow label={t('user.username')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.username}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={user.username}
                                onChange={e => updateUserField('username', e.target.value)}
                                error={!hasUserName()}
                            />
                        )}
                    </FormRow>

                    {/* Password */}
                    <FormRow label={t('user.password')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.password}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                type="password"
                                fullWidth
                                autoComplete="new-password"
                                value={user.password ?? ''}
                                onChange={e => updateUserField('password', e.target.value)}
                                error={isNewUser && !hasPassword()}
                            />
                        )}
                    </FormRow>

                    {/* Username POS */}
                    <FormRow label={t('user.usernamepos')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.usernamePos}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={user.usernamePos ?? ''}
                                onChange={e => updateUserField('usernamePos', e.target.value)}
                            />
                        )}
                    </FormRow>

                    {/* Employee Number */}
                    <FormRow label={t('user.employeenumber')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.employeeNumber}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={user.employeeNumber ?? ''}
                                onChange={e => updateUserField('employeeNumber', e.target.value)}
                            />
                        )}
                    </FormRow>

                    {/* SSN */}
                    <FormRow label={t('user.ssn')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.socialSecurityNumber}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={user.socialSecurityNumber ?? ''}
                                onChange={e => updateUserField('socialSecurityNumber', e.target.value)}
                            />
                        )}
                    </FormRow>

                    {/* Start Date */}
                    <FormRow label={t('user.startdate')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{formatDate(user.startDate)}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                type="date"
                                fullWidth
                                value={user.startDate ? user.startDate.substring(0, 10) : ''}
                                onChange={e => updateUserField('startDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    </FormRow>

                    {/* Active */}
                    <FormRow label={t('user.isactive')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>
                                {user.isActive ? t('common.true') : t('common.false')}
                            </Typography>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={user.isActive}
                                        onChange={e => updateUserField('isActive', e.target.checked)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </FormRow>

                    {/* Common Reports */}
                    <FormRow label={t('user.commonreports')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>
                                {user.isAllowedToViewCommonReports ? t('common.true') : t('common.false')}
                            </Typography>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!user.isAllowedToViewCommonReports}
                                        onChange={e => updateUserField('isAllowedToViewCommonReports', e.target.checked)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </FormRow>

                    <Divider />

                    {/* Addresses */}
                    {user.addresses.map(address => (
                        <FormRow key={address.addressType?.id} label={address.addressType?.typeName ?? ''}>
                            <AddressEditor
                                address={address}
                                isEditing={isEditing}
                                onChange={(field, value) => updateAddressField(address, field, value)}
                                onRemove={() => removeAddress(address)}
                            />
                        </FormRow>
                    ))}

                    {isEditing && availableAddressTypes.length > 0 && (
                        <FormRow label="">
                            <FormControl size="small" fullWidth>
                                <Select
                                    value=""
                                    displayEmpty
                                    onChange={e => {
                                        const at = addressTypes.find(a => a.id === e.target.value);
                                        if (at) addNewAddress(at);
                                    }}
                                >
                                    <MenuItem value=""><em>+ {t('user.newaddress')}</em></MenuItem>
                                    {availableAddressTypes.map(at => (
                                        <MenuItem key={at.id} value={at.id}>{at.typeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormRow>
                    )}

                    {/* Contact Infos */}
                    {user.contactInfos.map(info => {
                        const isEmail = info.contactInfoType?.systemTypeName === 'Email';
                        const emailHasError = isEmail && isEditing && (!info.value || !EMAIL_REGEX.test(info.value));
                        return (
                            <FormRow key={info.contactInfoType?.id} label={info.contactInfoType?.typeName ?? ''}>
                                <ContactInfoEditor
                                    contactInfo={info}
                                    isEditing={isEditing}
                                    hasError={emailHasError}
                                    errorText={emailHasError ? t('user.entervalidemail') : undefined}
                                    onChange={(field, value) => updateContactInfoField(info, field, value)}
                                    onRemove={() => removeContactInfo(info)}
                                />
                            </FormRow>
                        );
                    })}

                    {isEditing && availableContactInfoTypes.length > 0 && (
                        <FormRow label="">
                            <FormControl size="small" fullWidth error={!hasEmail()}>
                                <Select
                                    value=""
                                    displayEmpty
                                    onChange={e => {
                                        const ct = contactInfoTypes.find(c => c.id === e.target.value);
                                        if (ct) addNewContactInfo(ct);
                                    }}
                                >
                                    <MenuItem value=""><em>+ {t('user.newcontactinfo')}</em></MenuItem>
                                    {availableContactInfoTypes.map(ct => (
                                        <MenuItem key={ct.id} value={ct.id}>{ct.typeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormRow>
                    )}

                    <Divider />

                    {/* Locations */}
                    <FormRow label={t('user.locations')}>
                        {!isEditing ? (
                            <Box>
                                {[...user.locations]
                                    .sort((a, b) => a.locationName.localeCompare(b.locationName))
                                    .map(l => <Typography sx={sx14} key={l.id}>{l.locationName}</Typography>)}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={locations}
                                getOptionLabel={o => o.locationName}
                                value={user.locations}
                                onChange={(_, value) => updateUserField('locations', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('user.addlocation')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((l, i) => <Chip {...getTagProps({ index: i })} key={l.id} label={l.locationName} size="small" />)
                                }
                            />
                        )}
                    </FormRow>

                    {/* Brands */}
                    <FormRow label={t('user.brands')}>
                        {!isEditing ? (
                            <Box>
                                {[...user.brands]
                                    .sort((a, b) => a.brandDescription.localeCompare(b.brandDescription))
                                    .map(b => <Typography sx={sx14} key={b.id}>{b.brandDescription}</Typography>)}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={brands}
                                getOptionLabel={o => `${o.brandCode} ${o.brandDescription}`}
                                value={user.brands}
                                onChange={(_, value) => updateUserField('brands', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('user.addBrand')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((b, i) => (
                                        <Chip {...getTagProps({ index: i })} key={b.id} label={`${b.brandCode} ${b.brandDescription}`} size="small" />
                                    ))
                                }
                            />
                        )}
                    </FormRow>

                    {/* Role */}
                    <FormRow label={t('user.roles')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{user.role?.roleName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!hasRole()}>
                                <Select
                                    value={user.role?.id ?? ''}
                                    onChange={e => {
                                        const r = roles.find(x => x.id === e.target.value);
                                        updateUserField('role', r ?? null);
                                    }}
                                >
                                    {[...roles]
                                        .sort((a, b) => a.roleName.localeCompare(b.roleName))
                                        .map(r => (
                                            <MenuItem key={r.id} value={r.id}>{r.roleName}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>
                </Stack>
            </Box>

            {saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    );
}

interface AddressEditorProps {
    address: Address;
    isEditing: boolean;
    onChange: (field: string, value: string) => void;
    onRemove: () => void;
}

function AddressEditor({ address, isEditing, onChange, onRemove }: AddressEditorProps) {
    const { t } = useTranslation();
    const fields: { key: string; label: string }[] = [
        { key: 'addressLine1', label: t('address.addressline1') },
        { key: 'addressLine2', label: t('address.addressline2') },
        { key: 'addressLine3', label: t('address.addressline3') },
        { key: 'postalCode', label: t('address.postalCode') },
        { key: 'city', label: t('address.city') },
        { key: 'country', label: t('address.country') },
    ];

    if (!isEditing) {
        const parts = fields
            .map(f => address[f.key] as string | undefined)
            .filter(Boolean);
        return <Typography sx={sx14}>{parts.join(', ')}</Typography>;
    }

    return (
        <Stack spacing={1}>
            {fields.map(f => (
                <TextField
                    key={f.key}
                    size="small"
                    fullWidth
                    placeholder={f.label}
                    value={(address[f.key] as string | undefined) ?? ''}
                    onChange={e => onChange(f.key, e.target.value)}
                />
            ))}
            <Box>
                <Button size="small" color="error" variant="outlined" onClick={onRemove}>
                    Remove
                </Button>
            </Box>
        </Stack>
    );
}

interface ContactInfoEditorProps {
    contactInfo: ContactInfo;
    isEditing: boolean;
    hasError?: boolean;
    errorText?: string;
    onChange: (field: string, value: string) => void;
    onRemove: () => void;
}

function ContactInfoEditor({ contactInfo, isEditing, hasError, errorText, onChange, onRemove }: ContactInfoEditorProps) {
    if (!isEditing) {
        return <Typography sx={sx14}>{contactInfo.value as string}</Typography>;
    }
    return (
        <Box display="flex" gap={1}>
            <TextField
                size="small"
                fullWidth
                error={hasError}
                helperText={errorText}
                value={(contactInfo.value as string | undefined) ?? ''}
                onChange={e => onChange('value', e.target.value)}
            />
            <Button size="small" color="error" variant="outlined" onClick={onRemove}>
                Remove
            </Button>
        </Box>
    );
}

function formatDate(value: string | undefined): string {
    if (!value) return '';
    return value.substring(0, 10);
}
